import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Package, Plus, Search, Filter, Clock, CheckCircle, XCircle, Truck,
  AlertTriangle, ChevronRight, FileText, Loader2, ArrowLeft, RefreshCw,
  Calendar, Users, Box, Zap, GraduationCap, Wrench, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  'Pendente': { color: 'bg-amber-500', icon: Clock, label: 'Pendente' },
  'Em_Analise': { color: 'bg-blue-500', icon: Eye, label: 'Em análise' },
  'Aprovado': { color: 'bg-green-500', icon: CheckCircle, label: 'Aprovado' },
  'Parcialmente_Atendido': { color: 'bg-orange-500', icon: Package, label: 'Parcial' },
  'Em_Transito': { color: 'bg-purple-500', icon: Truck, label: 'Em Trânsito' },
  'Concluido': { color: 'bg-green-600', icon: CheckCircle, label: 'Concluído' },
  'Rejeitado': { color: 'bg-red-500', icon: XCircle, label: 'Rejeitado' }
};

const TIPOS_RECURSO = [
  { id: 'BBD', nome: 'Banco de Bens Doados', icon: Box, desc: 'Alimentos, higiene, roupa' },
  { id: 'BE', nome: 'Banco de Equipamentos', icon: Package, desc: 'Computadores, mobiliário' },
  { id: 'Energia', nome: 'Vale Energia', icon: Zap, desc: 'Garrafas de gás' },
  { id: 'Formacao', nome: 'Formação', icon: GraduationCap, desc: 'Cursos e workshops' },
  { id: 'Servicos', nome: 'Serviços', icon: Wrench, desc: 'Apoio gestão, jurídico' },
  { id: 'Outro', nome: 'Outros', icon: FileText, desc: 'Outros recursos' }
];

const CATEGORIAS_BBD = ['Higiene', 'Limpeza', 'Alimentação', 'Roupa', 'Calçado', 'Artigos Bebé', 'Fraldas', 'Outros'];
const TIPOS_EQUIPAMENTO = ['Computador', 'Portátil', 'Tablet', 'Impressora', 'Monitor', 'Telemóvel', 'Mobiliário', 'Outro'];
const URGENCIAS = [
  { value: 'Urgente', label: 'Urgente', color: 'text-red-600' },
  { value: 'Alta', label: 'Alta', color: 'text-orange-600' },
  { value: 'Media', label: 'Média', color: 'text-amber-600' },
  { value: 'Baixa', label: 'Baixa', color: 'text-green-600' }
];

export default function PortalPedidosPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [pedidos, setPedidos] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState('all');
  const [filtroTipo, setFiltroTipo] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [novoModalOpen, setNovoModalOpen] = useState(false);
  const [detalheModalOpen, setDetalheModalOpen] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  
  // Form state for new request
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    tipo_recurso: '',
    // BBD fields
    categoria_bbd: '',
    produto_especifico: '',
    quantidade: '',
    unidade: 'unidades',
    destinatario: 'Instituicao',
    num_familias: '',
    num_pessoas: '',
    num_criancas: '',
    // BE fields
    tipo_equipamento: '',
    especificacoes: '',
    uso_previsto: '',
    // Energia fields
    tipo_gas: 'Butano',
    num_garrafas: '',
    familias_beneficiarias: '',
    // Formacao fields
    area_formacao: '',
    tema_especifico: '',
    num_participantes: '',
    modalidade: 'Indiferente',
    // Common fields
    data_necessidade: '',
    urgencia: 'Media',
    justificacao: ''
  });

  useEffect(() => {
    loadPedidos();
    // Check if should open new request modal (from /portal/pedidos/novo or ?novo=true)
    if (searchParams.get('novo') === 'true' || window.location.pathname.includes('/novo')) {
      setNovoModalOpen(true);
    }
  }, [searchParams]);

  const loadPedidos = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/pedidos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPedidos(data);
      } else if (response.status === 401) {
        navigate('/portal/login');
      }
    } catch (error) {
      console.error('Error loading pedidos:', error);
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPedido = async () => {
    if (!formData.tipo_recurso || !formData.urgencia || !formData.justificacao) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    if (formData.justificacao.length < 50) {
      toast.error('A justificação deve ter pelo menos 50 caracteres');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('portal_token');
      
      // Pre-validate the request
      const categoria = formData.categoria_bbd || formData.tipo_equipamento || formData.tipo_recurso;
      const validacaoRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portal/pedidos/validar?categoria=${encodeURIComponent(categoria)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      let validacao = { valido: true, avisos: [], erros: [] };
      if (validacaoRes.ok) {
        validacao = await validacaoRes.json();
      }
      
      // If validation has errors, show warning and ask for confirmation
      if (validacao.erros?.length > 0) {
        const confirmar = window.confirm(
          `⚠️ ATENÇÃO: ${validacao.erros[0]}\n\n${validacao.sugestao || ''}\n\nDeseja continuar mesmo assim?`
        );
        if (!confirmar) {
          setSubmitting(false);
          return;
        }
      }
      
      // Submit the request
      let response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/pedidos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.status === 409) {
        const avisoEstado = await response.json();
        const confirmarEstado = window.confirm(`⚠️ ${avisoEstado.detail}`);
        if (!confirmarEstado) {
          setSubmitting(false);
          return;
        }
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/pedidos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...formData, confirmado_em_analise: true })
        });
      }
      
      if (response.ok) {
        const data = await response.json();
        
        // Show success with any warnings
        if (data.validacao?.avisos?.length > 0) {
          toast.warning(
            `Pedido ${data.codigo} submetido. Aviso: ${data.validacao.avisos[0]}`,
            { duration: 6000 }
          );
        } else {
          toast.success(`Pedido ${data.codigo} submetido com sucesso!`);
        }
        
        setNovoModalOpen(false);
        resetForm();
        loadPedidos();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao submeter pedido');
      }
    } catch (error) {
      toast.error('Erro ao submeter pedido');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_recurso: '',
      categoria_bbd: '',
      produto_especifico: '',
      quantidade: '',
      unidade: 'unidades',
      destinatario: 'Instituicao',
      num_familias: '',
      num_pessoas: '',
      num_criancas: '',
      tipo_equipamento: '',
      especificacoes: '',
      uso_previsto: '',
      tipo_gas: 'Butano',
      num_garrafas: '',
      familias_beneficiarias: '',
      area_formacao: '',
      tema_especifico: '',
      num_participantes: '',
      modalidade: 'Indiferente',
      data_necessidade: '',
      urgencia: 'Media',
      justificacao: ''
    });
    setStep(1);
  };

  const openDetail = (pedido) => {
    setSelectedPedido(pedido);
    setDetalheModalOpen(true);
  };

  const filteredPedidos = pedidos.filter(p => {
    if (filtroStatus !== 'all' && p.status !== filtroStatus) return false;
    if (filtroTipo !== 'all' && p.tipo_recurso !== filtroTipo) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return p.codigo?.toLowerCase().includes(search) ||
             p.tipo_recurso?.toLowerCase().includes(search);
    }
    return true;
  });

  const getStatusCounts = () => {
    const counts = { total: pedidos.length };
    Object.keys(STATUS_CONFIG).forEach(status => {
      counts[status] = pedidos.filter(p => p.status === status).length;
    });
    return counts;
  };

  const counts = getStatusCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pedidos-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-pedidos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-purple-600" />
            Pedidos de Apoio
          </h1>
          <p className="text-slate-500">Gerir e acompanhar pedidos de recursos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadPedidos}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setNovoModalOpen(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Novo Pedido
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-purple-600">{counts.total}</p>
            <p className="text-sm text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-amber-600">{counts.Pendente || 0}</p>
            <p className="text-sm text-slate-500">Pendentes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-green-600">{counts.Aprovado || 0}</p>
            <p className="text-sm text-slate-500">Aprovados</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-purple-600">{counts.Em_Transito || 0}</p>
            <p className="text-sm text-slate-500">Em Trânsito</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-green-700">{counts.Concluido || 0}</p>
            <p className="text-sm text-slate-500">Concluídos</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tipos</SelectItem>
                {TIPOS_RECURSO.map(tipo => (
                  <SelectItem key={tipo.id} value={tipo.id}>{tipo.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Pedidos List */}
      <Card>
        <CardContent className="pt-6">
          {filteredPedidos.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">
                {pedidos.length === 0 ? 'Ainda não tem pedidos' : 'Nenhum pedido encontrado'}
              </p>
              <Button onClick={() => setNovoModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Pedido
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Código</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Urgência</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPedidos.map((pedido) => {
                  const statusConfig = STATUS_CONFIG[pedido.status] || STATUS_CONFIG['Pendente'];
                  const StatusIcon = statusConfig.icon;
                  const tipoInfo = TIPOS_RECURSO.find(t => t.id === pedido.tipo_recurso);
                  const TipoIcon = tipoInfo?.icon || Package;
                  
                  return (
                    <TableRow key={pedido.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(pedido)}>
                      <TableCell className="font-mono font-medium">{pedido.codigo}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TipoIcon className="w-4 h-4 text-slate-400" />
                          <span>{tipoInfo?.nome || pedido.tipo_recurso}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(pedido.created_at).toLocaleDateString('pt-PT')}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={URGENCIAS.find(u => u.value === pedido.urgencia)?.color}>
                          {pedido.urgencia}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig.color} text-white`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openDetail(pedido); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Request Modal */}
      <Dialog open={novoModalOpen} onOpenChange={setNovoModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Pedido de Recursos</DialogTitle>
            <DialogDescription>
              Passo {step} de 3
            </DialogDescription>
          </DialogHeader>

          <Progress value={(step / 3) * 100} className="mb-4" />

          {/* Step 1: Select Resource Type */}
          {step === 1 && (
            <div className="space-y-4">
              <Label className="text-base font-medium">Selecione o tipo de recurso</Label>
              <div className="grid grid-cols-2 gap-3">
                {TIPOS_RECURSO.map((tipo) => {
                  const Icon = tipo.icon;
                  const isSelected = formData.tipo_recurso === tipo.id;
                  return (
                    <button
                      key={tipo.id}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tipo_recurso: tipo.id }))}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        isSelected 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-slate-200 hover:border-purple-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-purple-600' : 'text-slate-400'}`} />
                      <p className="font-medium">{tipo.nome}</p>
                      <p className="text-sm text-slate-500">{tipo.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Resource Details */}
          {step === 2 && (
            <div className="space-y-4">
              {/* BBD Fields */}
              {formData.tipo_recurso === 'BBD' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Categoria *</Label>
                      <Select value={formData.categoria_bbd} onValueChange={(v) => setFormData(prev => ({ ...prev, categoria_bbd: v }))}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIAS_BBD.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Produto Específico</Label>
                      <Input
                        value={formData.produto_especifico}
                        onChange={(e) => setFormData(prev => ({ ...prev, produto_especifico: e.target.value }))}
                        placeholder="Ex: Detergente líquido"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Quantidade *</Label>
                      <Input
                        type="number"
                        value={formData.quantidade}
                        onChange={(e) => setFormData(prev => ({ ...prev, quantidade: e.target.value }))}
                        placeholder="Ex: 50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unidade</Label>
                      <Select value={formData.unidade} onValueChange={(v) => setFormData(prev => ({ ...prev, unidade: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unidades">Unidades</SelectItem>
                          <SelectItem value="kg">Kg</SelectItem>
                          <SelectItem value="litros">Litros</SelectItem>
                          <SelectItem value="caixas">Caixas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Destinatários *</Label>
                    <Select value={formData.destinatario} onValueChange={(v) => setFormData(prev => ({ ...prev, destinatario: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Instituicao">Uso na Instituição</SelectItem>
                        <SelectItem value="Familias">Distribuição a Famílias</SelectItem>
                        <SelectItem value="Ambos">Ambos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(formData.destinatario === 'Familias' || formData.destinatario === 'Ambos') && (
                    <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className="space-y-2">
                        <Label>Nº Famílias</Label>
                        <Input type="number" value={formData.num_familias} onChange={(e) => setFormData(prev => ({ ...prev, num_familias: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Nº Pessoas</Label>
                        <Input type="number" value={formData.num_pessoas} onChange={(e) => setFormData(prev => ({ ...prev, num_pessoas: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Nº Crianças</Label>
                        <Input type="number" value={formData.num_criancas} onChange={(e) => setFormData(prev => ({ ...prev, num_criancas: e.target.value }))} />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* BE Fields */}
              {formData.tipo_recurso === 'BE' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de Equipamento *</Label>
                      <Select value={formData.tipo_equipamento} onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_equipamento: v }))}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {TIPOS_EQUIPAMENTO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Quantidade *</Label>
                      <Input type="number" value={formData.quantidade} onChange={(e) => setFormData(prev => ({ ...prev, quantidade: e.target.value }))} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Especificações Técnicas</Label>
                    <Textarea
                      value={formData.especificacoes}
                      onChange={(e) => setFormData(prev => ({ ...prev, especificacoes: e.target.value }))}
                      placeholder="Ex: Mínimo 8GB RAM, SSD 256GB"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Uso Previsto *</Label>
                    <Textarea
                      value={formData.uso_previsto}
                      onChange={(e) => setFormData(prev => ({ ...prev, uso_previsto: e.target.value }))}
                      placeholder="Descreva para que será utilizado o equipamento"
                      rows={2}
                    />
                  </div>
                </>
              )}

              {/* Energia Fields */}
              {formData.tipo_recurso === 'Energia' && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Gás *</Label>
                    <Select value={formData.tipo_gas} onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_gas: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Butano">Butano</SelectItem>
                        <SelectItem value="Propano">Propano</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nº Garrafas *</Label>
                    <Input type="number" value={formData.num_garrafas} onChange={(e) => setFormData(prev => ({ ...prev, num_garrafas: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Nº Famílias *</Label>
                    <Input type="number" value={formData.familias_beneficiarias} onChange={(e) => setFormData(prev => ({ ...prev, familias_beneficiarias: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Formacao Fields */}
              {formData.tipo_recurso === 'Formacao' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Área *</Label>
                      <Select value={formData.area_formacao} onValueChange={(v) => setFormData(prev => ({ ...prev, area_formacao: v }))}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Gestao">Gestão</SelectItem>
                          <SelectItem value="Financeira">Financeira</SelectItem>
                          <SelectItem value="RH">Recursos Humanos</SelectItem>
                          <SelectItem value="Informatica">Informática</SelectItem>
                          <SelectItem value="Outra">Outra</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tema Específico</Label>
                      <Input value={formData.tema_especifico} onChange={(e) => setFormData(prev => ({ ...prev, tema_especifico: e.target.value }))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nº Participantes *</Label>
                      <Input type="number" value={formData.num_participantes} onChange={(e) => setFormData(prev => ({ ...prev, num_participantes: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Modalidade</Label>
                      <Select value={formData.modalidade} onValueChange={(v) => setFormData(prev => ({ ...prev, modalidade: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Presencial">Presencial</SelectItem>
                          <SelectItem value="Online">Online</SelectItem>
                          <SelectItem value="Indiferente">Indiferente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {/* Servicos / Outro - just justification */}
              {(formData.tipo_recurso === 'Servicos' || formData.tipo_recurso === 'Outro') && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    Descreva detalhadamente o que precisa no campo de justificação do próximo passo.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Justification */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data de Necessidade</Label>
                  <Input type="date" value={formData.data_necessidade} onChange={(e) => setFormData(prev => ({ ...prev, data_necessidade: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Grau de Urgência *</Label>
                  <Select value={formData.urgencia} onValueChange={(v) => setFormData(prev => ({ ...prev, urgencia: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {URGENCIAS.map(u => (
                        <SelectItem key={u.value} value={u.value}>
                          <span className={u.color}>{u.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Justificação Detalhada * (mín. 50 caracteres)</Label>
                <Textarea
                  value={formData.justificacao}
                  onChange={(e) => setFormData(prev => ({ ...prev, justificacao: e.target.value }))}
                  placeholder="Explique por que precisa deste recurso, quantas pessoas serão beneficiadas e qual o impacto esperado..."
                  rows={5}
                />
                <p className={`text-xs ${formData.justificacao.length >= 50 ? 'text-green-600' : 'text-slate-400'}`}>
                  {formData.justificacao.length}/50 caracteres
                </p>
              </div>

              {/* Summary */}
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-medium mb-2">Resumo do Pedido</h4>
                <div className="text-sm space-y-1">
                  <p><strong>Tipo:</strong> {TIPOS_RECURSO.find(t => t.id === formData.tipo_recurso)?.nome}</p>
                  {formData.quantidade && <p><strong>Quantidade:</strong> {formData.quantidade} {formData.unidade}</p>}
                  {formData.categoria_bbd && <p><strong>Categoria:</strong> {formData.categoria_bbd}</p>}
                  {formData.tipo_equipamento && <p><strong>Equipamento:</strong> {formData.tipo_equipamento}</p>}
                  <p><strong>Urgência:</strong> {formData.urgencia}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
            )}
            <Button variant="outline" onClick={() => { setNovoModalOpen(false); resetForm(); }}>
              Cancelar
            </Button>
            {step < 3 ? (
              <Button 
                onClick={() => setStep(step + 1)} 
                disabled={step === 1 && !formData.tipo_recurso}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Seguinte
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmitPedido} disabled={submitting} className="bg-green-600 hover:bg-green-700">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Submeter Pedido
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detalheModalOpen} onOpenChange={setDetalheModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Pedido {selectedPedido?.codigo}</DialogTitle>
          </DialogHeader>
          
          {selectedPedido && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge className={`${STATUS_CONFIG[selectedPedido.status]?.color || 'bg-slate-500'} text-white`}>
                  {STATUS_CONFIG[selectedPedido.status]?.label || selectedPedido.status}
                </Badge>
                <Badge variant="outline" className={URGENCIAS.find(u => u.value === selectedPedido.urgencia)?.color}>
                  {selectedPedido.urgencia}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Tipo</p>
                  <p className="font-medium">{TIPOS_RECURSO.find(t => t.id === selectedPedido.tipo_recurso)?.nome}</p>
                </div>
                <div>
                  <p className="text-slate-500">Data Submissão</p>
                  <p className="font-medium">{new Date(selectedPedido.created_at).toLocaleDateString('pt-PT')}</p>
                </div>
                {selectedPedido.quantidade && (
                  <div>
                    <p className="text-slate-500">Quantidade</p>
                    <p className="font-medium">{selectedPedido.quantidade} {selectedPedido.unidade || 'unidades'}</p>
                  </div>
                )}
                {selectedPedido.data_necessidade && (
                  <div>
                    <p className="text-slate-500">Data Necessidade</p>
                    <p className="font-medium">{new Date(selectedPedido.data_necessidade).toLocaleDateString('pt-PT')}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-slate-500 text-sm mb-1">Justificação</p>
                <p className="text-sm bg-slate-50 p-3 rounded-lg">{selectedPedido.justificacao}</p>
              </div>

              {/* Timeline */}
              {selectedPedido.timeline && selectedPedido.timeline.length > 0 && (
                <div>
                  <p className="text-slate-500 text-sm mb-2">Histórico</p>
                  <div className="space-y-2">
                    {selectedPedido.timeline.map((event, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                        <span className="text-slate-400">{new Date(event.data).toLocaleString('pt-PT')}</span>
                        <span>{event.descricao}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetalheModalOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
